import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Bell, MessageSquare, FileText, Send, ChevronDown, ChevronUp, Check, X as XIcon,
  Users, Mail, ShieldCheck, Plane, MapPin, Calendar, AtSign, ThumbsUp, MessageCircle, Building2, Inbox as InboxIcon, ArrowLeft,
} from "lucide-react";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";

const STATUS_TONE = { pending: "cyan", accepted: "green", declined: "red" };

const TYPE_ICON = {
  fbo_mention: AtSign,
  post_comment: MessageCircle,
  post_like: ThumbsUp,
  contract_application: FileText,
};

function relTime(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    if (s < 604800) return `${Math.floor(s / 86400)}d`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (u && u.startsWith("/") ? `${backendUrl}${u}` : u);

export default function Inbox() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("user") ? "messages" : "notifications");

  // -------- Notifications --------
  const [notifications, setNotifications] = useState([]);
  const [unreadN, setUnreadN] = useState(0);

  const loadNotifications = async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 50 } });
      setNotifications(r.data.notifications || []);
      setUnreadN(r.data.unread_count || 0);
    } catch { /* ignore */ }
  };

  const markRead = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnreadN((u) => Math.max(0, u - 1));
    }
    if (n.link) nav(n.link);
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setUnreadN(0);
      toast.success("All caught up");
    } catch { /* ignore */ }
  };

  // -------- Messages --------
  const [conversations, setConversations] = useState([]);
  const [unreadM, setUnreadM] = useState(0);
  const [openConv, setOpenConv] = useState(null);      // conversation object
  const [openMsgs, setOpenMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const loadConversations = async () => {
    try {
      const r = await api.get("/messages/conversations");
      setConversations(r.data.conversations || []);
      setUnreadM(r.data.unread_total || 0);
    } catch { /* ignore */ }
  };

  const openConversation = async (conv) => {
    try {
      const r = await api.get(`/messages/with/${conv.other.id}`);
      setOpenConv({ ...conv, other: r.data.other, id: r.data.conversation_id });
      setOpenMsgs(r.data.messages || []);
      // Optimistically clear the unread badge locally
      setConversations((prev) => prev.map((c) => (c.id === conv.id ? { ...c, unread: 0 } : c)));
      setUnreadM((u) => Math.max(0, u - (conv.unread || 0)));
    } catch (e) { toast.error("Failed to load conversation"); }
  };

  const sendDraft = async () => {
    if (!draft.trim() || !openConv?.other?.id) return;
    setSending(true);
    try {
      const r = await api.post(`/messages/with/${openConv.other.id}`, { text: draft.trim() });
      setOpenMsgs((prev) => [...prev, r.data]);
      setDraft("");
      // Refresh conversations list to bump this convo to top
      loadConversations();
    } catch (e) {
      const status = e?.response?.status;
      if (status !== 402 && status !== 403) {
        toast.error(errMsg(e, "Send failed"));
      }
    } finally { setSending(false); }
  };

  // -------- Contracts (existing) --------
  const [posted, setPosted] = useState([]);
  const [applied, setApplied] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [applications, setApplications] = useState({});

  const loadContracts = async () => {
    try {
      const [p, a] = await Promise.all([
        api.get("/contracts/mine"),
        api.get("/applications/mine"),
      ]);
      setPosted(p.data);
      setApplied(a.data);
    } catch { /* ignore */ }
  };

  const loadApplications = async (cid) => {
    if (applications[cid]) return;
    try {
      const r = await api.get(`/contracts/${cid}/applications`);
      setApplications((prev) => ({ ...prev, [cid]: r.data }));
    } catch { toast.error("Failed to load applications"); }
  };

  const toggle = (cid) => {
    if (expanded === cid) { setExpanded(null); return; }
    setExpanded(cid);
    loadApplications(cid);
  };

  const setStatus = async (aid, status, cid) => {
    try {
      await api.patch(`/applications/${aid}/status`, { status });
      setApplications((prev) => ({
        ...prev,
        [cid]: prev[cid].map((a) => (a.id === aid ? { ...a, status } : a)),
      }));
      toast.success(`Application ${status}`);
    } catch { toast.error("Failed"); }
  };

  // Initial fetch on user change
  useEffect(() => {
    if (!user) return;
    loadNotifications();
    loadConversations();
    loadContracts();
  }, [user?.id]);

  // Poll unread counts every 30s
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      loadNotifications();
      loadConversations();
    }, 30000);
    return () => clearInterval(id);
  }, [user?.id]);

  // Deep-link: if ?user=<uid> was passed (e.g. from a listing's "Message the plane"),
  // switch to the Messages tab and auto-open that conversation once loaded.
  useEffect(() => {
    const targetUid = searchParams.get("user");
    if (!targetUid || !user || openConv || conversations.length === 0) return;
    const match = conversations.find((c) => c.other?.id === targetUid);
    if (match) {
      setTab("messages");
      openConversation(match);
      // Clear the query param so refresh doesn't re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, user?.id, conversations, openConv]);

  if (!user) return <div className="max-w-4xl mx-auto px-6 py-12 text-white/50">Auth required</div>;

  const receivedCount = posted.reduce((n, p) => n + (p.application_count || 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? nav(-1) : nav("/dashboard"))}
        data-testid="inbox-back-btn"
        className="mb-6 inline-flex items-center gap-2 text-white/55 hover:text-white text-[12px] tracking-widest uppercase mono transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </button>
      <SectionTitle overline="INBOX" title="All your PJP activity." subtitle="Notifications, direct messages, and contract activity — one place." />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="inbox-tabs">
        <TabBtn active={tab === "notifications"} onClick={() => setTab("notifications")} testId="tab-notifications">
          <Bell className="h-3.5 w-3.5" /> Notifications
          {unreadN > 0 && <span className="ml-1 mono text-[10px] px-1.5 py-0.5 rounded-full bg-sky text-obsidian">{unreadN}</span>}
        </TabBtn>
        <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testId="tab-messages">
          <MessageSquare className="h-3.5 w-3.5" /> Messages
          {unreadM > 0 && <span className="ml-1 mono text-[10px] px-1.5 py-0.5 rounded-full bg-sky text-obsidian">{unreadM}</span>}
        </TabBtn>
        <TabBtn active={tab === "contracts"} onClick={() => setTab("contracts")} testId="tab-contracts">
          <FileText className="h-3.5 w-3.5" /> Contracts
          {(receivedCount + applied.length) > 0 && <span className="ml-1 mono text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/70">{receivedCount + applied.length}</span>}
        </TabBtn>
      </div>

      {tab === "notifications" && (
        <div className="space-y-2" data-testid="notifications-list">
          {notifications.length > 0 && (
            <div className="flex justify-end mb-2">
              <button onClick={markAllRead} disabled={unreadN === 0} className="text-xs text-white/60 hover:text-white mono tracking-wider disabled:opacity-40" data-testid="btn-mark-all-read">
                MARK ALL READ
              </button>
            </div>
          )}
          {notifications.length === 0 && <EmptyState icon={InboxIcon} title="No notifications yet" hint="Tag an FBO in a post or comment on someone else's post — you'll see activity here." />}
          {notifications.map((n) => {
            const Icon = TYPE_ICON[n.type] || Bell;
            return (
              <button
                key={n.id}
                onClick={() => markRead(n)}
                data-testid={`notif-${n.id}`}
                className={`w-full text-left card !p-4 flex items-start gap-3 transition-colors ${n.is_read ? "opacity-70 hover:opacity-100" : "border-sky/40 bg-sky/[0.04]"}`}
              >
                <div className="relative flex-shrink-0">
                  {n.actor_avatar_url ? (
                    <img src={abs(n.actor_avatar_url)} alt="" className="h-10 w-10 rounded-full object-cover border border-white/15" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-sky/20 border border-sky/40 grid place-items-center">
                      <Icon className="h-4 w-4 text-sky" />
                    </div>
                  )}
                  <div className="absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full bg-obsidian border border-sky/40 grid place-items-center">
                    <Icon className="h-3 w-3 text-sky" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-white text-sm truncate">{n.title}</div>
                    {!n.is_read && <span className="mt-1 h-2 w-2 rounded-full bg-sky flex-shrink-0" />}
                  </div>
                  <div className="text-sm text-white/70 mt-0.5 truncate">{n.message}</div>
                  <div className="mono text-[10px] text-white/40 mt-1">{relTime(n.created_at)}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {tab === "messages" && (
        openConv ? (
          <ConversationView
            conv={openConv}
            messages={openMsgs}
            draft={draft}
            setDraft={setDraft}
            onBack={() => { setOpenConv(null); setOpenMsgs([]); loadConversations(); }}
            onSend={sendDraft}
            sending={sending}
            meId={user.id}
          />
        ) : (
          <div className="space-y-2" data-testid="conversations-list">
            {conversations.length === 0 && <EmptyState icon={MessageSquare} title="No messages yet" hint="Message a member from their profile — every user can DM anyone in the network." />}
            {conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => openConversation(c)}
                data-testid={`conv-${c.id}`}
                className={`w-full text-left card !p-4 flex items-center gap-3 transition-colors ${c.unread > 0 ? "border-sky/40 bg-sky/[0.04]" : ""}`}
              >
                {c.other?.avatar_url ? (
                  <img src={abs(c.other.avatar_url)} alt="" className="h-11 w-11 rounded-full object-cover border border-white/15 flex-shrink-0" />
                ) : (
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-sky/40 to-sky/10 border border-sky/40 grid place-items-center mono text-xs font-bold text-white flex-shrink-0">
                    {(c.other?.name || "?").split(" ").map(s => s.charAt(0)).slice(0,2).join("")}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-white truncate flex items-center gap-1.5">
                      {c.other?.name}
                      {c.other?.faa_verified && <ShieldCheck className="h-3.5 w-3.5 text-avail" />}
                    </div>
                    <div className="mono text-[10px] text-white/40 flex-shrink-0">{c.last_message_at ? relTime(c.last_message_at) : ""}</div>
                  </div>
                  <div className="text-sm text-white/60 truncate">{c.last_message || <span className="italic">No messages yet</span>}</div>
                </div>
                {c.unread > 0 && (
                  <span className="mono text-[10px] px-2 py-0.5 rounded-full bg-sky text-obsidian font-bold flex-shrink-0">{c.unread}</span>
                )}
              </button>
            ))}
          </div>
        )
      )}

      {tab === "contracts" && (
        <ContractsPanel
          posted={posted} applied={applied}
          expanded={expanded} toggle={toggle}
          applications={applications} setStatus={setStatus}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold tracking-tight transition-all min-h-[44px] ${
        active
          ? "bg-sky/20 border border-sky/50 text-white shadow-[0_0_16px_rgba(75,156,255,0.25)]"
          : "bg-white/[0.04] border border-white/10 text-white/70 hover:text-white hover:border-white/25"
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="card !p-10 text-center">
      <Icon className="h-8 w-8 mx-auto text-sky mb-3" />
      <div className="font-display text-xl text-white mb-1">{title}</div>
      <div className="text-white/50 text-sm">{hint}</div>
    </div>
  );
}

function ConversationView({ conv, messages, draft, setDraft, onSend, sending, meId, onBack }) {
  const ref = React.useRef(null);
  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: "smooth" }); }, [messages.length]);
  return (
    <div className="card !p-0 overflow-hidden flex flex-col h-[70vh]" data-testid="conversation-view">
      <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-black/25">
        <button onClick={onBack} className="text-white/60 hover:text-white text-sm mono" data-testid="btn-back-conversations">← BACK</button>
        {conv.other?.avatar_url ? (
          <img src={abs(conv.other.avatar_url)} alt="" className="h-9 w-9 rounded-full object-cover border border-white/15" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-sky/20 border border-sky/40 grid place-items-center mono text-xs font-bold text-white">
            {(conv.other?.name || "?").split(" ").map(s => s.charAt(0)).slice(0,2).join("")}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-semibold text-white truncate flex items-center gap-1.5">
            {conv.other?.name}
            {conv.other?.faa_verified && <ShieldCheck className="h-3.5 w-3.5 text-avail" />}
          </div>
          <div className="mono text-[10px] text-white/40 uppercase tracking-wider">{conv.other?.role || "Member"}</div>
        </div>
      </div>

      <div ref={ref} className="flex-1 overflow-y-auto p-4 space-y-3 bg-black/15">
        {messages.length === 0 && <div className="text-center text-white/40 py-10">Start the conversation.</div>}
        {messages.map((m) => {
          const mine = m.sender_id === meId;
          const showTripCard = m.ai_intent && (m.ai_intent === "trip_request" || m.ai_intent === "service_request") && m.ai_trip;
          return (
            <React.Fragment key={m.id}>
              <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[85%] sm:max-w-[70%] px-3.5 py-2.5 rounded-2xl ${
                  mine
                    ? "bg-sky text-obsidian rounded-br-sm"
                    : "bg-white/[0.06] text-white rounded-bl-sm border border-white/10"
                }`}>
                  <div className="text-sm whitespace-pre-wrap break-words" style={{ fontSize: 15, lineHeight: 1.4 }}>{m.text}</div>
                  <div className={`mono text-[10px] mt-0.5 ${mine ? "text-obsidian/70" : "text-white/40"}`}>{relTime(m.created_at)}</div>
                </div>
              </div>
              {showTripCard && (
                <TripRequestCard
                  message={m}
                  otherUserId={conv.other?.id}
                  otherName={conv.other?.name}
                  onCreated={() => toast.success("Trip request created — check your contracts.")}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="border-t border-white/10 p-3 bg-black/30 flex gap-2 items-end" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
        <textarea
          data-testid="input-message"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          rows={1}
          placeholder="Write a message…"
          className="flex-1 bg-black/40 border border-white/10 rounded-2xl px-3.5 py-2.5 text-white placeholder:text-white/35 outline-none focus:border-sky/50 resize-none"
          style={{ fontSize: 16 }}
        />
        <button
          onClick={onSend}
          disabled={sending || !draft.trim()}
          data-testid="btn-send-message"
          className="px-4 py-2.5 rounded-full font-semibold text-sm disabled:opacity-40 min-h-[44px] min-w-[44px] flex items-center gap-1.5"
          style={{ background: "#4B9CFF", color: "#0A0F1A" }}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * TripRequestCard — inline in-thread card rendered when the DM analyzer
 * (Claude Haiku 4.5) classifies a message as a trip_request or
 * service_request. Either party can review the extracted fields and click
 * CREATE TRIP REQUEST to spawn a formal contract that funnels the deal
 * through PJP (secured payment via PJP Wallet).
 */
function TripRequestCard({ message, otherUserId, otherName, onCreated }) {
  const trip = message.ai_trip || {};
  const [busy, setBusy] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const [origin, setOrigin] = React.useState(trip.origin_hint || "");
  const [dest, setDest] = React.useState(trip.dest_hint || "");
  const [when, setWhen] = React.useState(trip.when_hint || "");
  const [aircraft, setAircraft] = React.useState(trip.aircraft_hint || "");
  const [role, setRole] = React.useState(trip.role_needed || "");
  if (dismissed) return null;

  const create = async () => {
    setBusy(true);
    try {
      // Best-effort parse of the free-text "when" hint. Fall back to today (UTC
      // date only) so the backend's required start_date validator is satisfied.
      let startDate = "";
      if (when) {
        const t = Date.parse(when);
        if (!Number.isNaN(t)) startDate = new Date(t).toISOString().slice(0, 10);
      }
      if (!startDate) startDate = new Date().toISOString().slice(0, 10);

      // Spawn a formal contract linked to this DM. Both parties see it.
      await api.post("/contracts", {
        role_needed: role || "charter",
        title: (trip.summary || "Trip request from DM").slice(0, 120),
        description: `Origin: ${origin || "TBD"}\nDest: ${dest || "TBD"}\nWhen: ${when || "TBD"}\nAircraft: ${aircraft || "TBD"}\n\nOriginating message: "${message.text}"`,
        start_date: startDate,
        departure: origin || undefined,
        destination: dest || undefined,
        aircraft_type: aircraft || undefined,
        counterparty_user_id: otherUserId,
        source: "dm_ai",
        source_message_id: message.id,
      });
      onCreated && onCreated();
      setDismissed(true);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't create trip request"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex justify-start" data-testid={`trip-card-${message.id}`}>
      <div className="max-w-[92%] sm:max-w-[80%] rounded-2xl border border-cyan/40 bg-cyan/[0.06] p-3 shadow-[0_0_24px_-8px_rgba(75,156,255,0.5)]">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-1.5 w-1.5 rounded-full bg-cyan animate-pulse" />
          <div className="mono text-[9px] tracking-[0.24em] uppercase text-cyan">// PJP AI · TRIP REQUEST DETECTED</div>
        </div>
        <div className="text-[13px] text-white/80 mb-3 leading-snug">
          {trip.summary || "It looks like you're setting up a flight. Formalize it in PJP so payment is secured through the wallet."}
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Origin (ICAO)" className="input-hud text-[12px]" data-testid={`trip-origin-${message.id}`} />
          <input value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Destination" className="input-hud text-[12px]" data-testid={`trip-dest-${message.id}`} />
          <input value={when} onChange={(e) => setWhen(e.target.value)} placeholder="When" className="input-hud text-[12px]" data-testid={`trip-when-${message.id}`} />
          <input value={aircraft} onChange={(e) => setAircraft(e.target.value)} placeholder="Aircraft" className="input-hud text-[12px]" data-testid={`trip-aircraft-${message.id}`} />
          <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role needed" className="input-hud text-[12px] col-span-2" data-testid={`trip-role-${message.id}`} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setDismissed(true)}
            data-testid={`trip-dismiss-${message.id}`}
            className="mono text-[10px] tracking-widest uppercase text-white/50 hover:text-white px-2 py-1"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={create}
            disabled={busy}
            data-testid={`trip-create-${message.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan text-obsidian mono text-[10px] font-bold tracking-widest uppercase shadow-[0_0_16px_-4px_rgba(75,156,255,0.7)] hover:brightness-105 disabled:opacity-60"
          >
            Create Trip Request
          </button>
        </div>
        <div className="mt-2 text-[10px] text-white/40 leading-snug">
          Business inside PJP = secured payment via PJP Wallet.
        </div>
      </div>
    </div>
  );
}



function ContractsPanel({ posted, applied, expanded, toggle, applications, setStatus }) {
  const [sub, setSub] = useState("received");
  return (
    <>
      <div className="flex gap-2 mb-4">
        <button data-testid="sub-tab-received" onClick={() => setSub("received")} className={`btn btn-sm ${sub === "received" ? "btn-primary" : ""}`}>Received ({posted.reduce((n, p) => n + (p.application_count || 0), 0)})</button>
        <button data-testid="sub-tab-sent" onClick={() => setSub("sent")} className={`btn btn-sm ${sub === "sent" ? "btn-primary" : ""}`}>Sent ({applied.length})</button>
      </div>
      {sub === "received" && (
        <div className="space-y-4">
          {posted.length === 0 && <EmptyState icon={FileText} title="No contracts posted yet" hint="Post a contract from the Contracts board and manage applicants here." />}
          {posted.map((c) => (
            <Card key={c.id} testId={`posted-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <Badge tone="cyan">{(c.role_needed || "").replace("_", " ").toUpperCase()}</Badge>
                    <div className="mono text-[10px] tracking-widest text-white/50 uppercase">{c.status}</div>
                  </div>
                  <h3 className="font-display text-xl font-semibold tracking-tight">{c.title}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm text-white/70 flex-wrap">
                    <span className="mono flex items-center gap-1.5"><Plane className="h-3.5 w-3.5 text-sky" />{c.aircraft_type}</span>
                    <span className="mono flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-sky" />{c.departure} → {c.destination}</span>
                    <span className="mono flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-sky" />{c.start_date}</span>
                  </div>
                </div>
                <button data-testid={`toggle-${c.id}`} onClick={() => toggle(c.id)} className="btn btn-sm">
                  <Users className="h-3.5 w-3.5" />
                  {c.application_count} {c.application_count === 1 ? "APPLICANT" : "APPLICANTS"}
                  {expanded === c.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              </div>
              {expanded === c.id && (
                <div className="mt-6 pt-6 border-t border-white/[0.06] space-y-3">
                  {(applications[c.id] || []).length === 0 && (
                    <div className="text-white/50 text-sm">No applications yet.</div>
                  )}
                  {(applications[c.id] || []).map((a) => (
                    <div key={a.id} data-testid={`app-${a.id}`} className="border border-white/10 rounded-lg p-4 bg-black/30">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-[220px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-display text-lg font-semibold">{a.applicant_name}</div>
                            {a.faa_verified && <Badge tone="green" pulse><ShieldCheck className="h-3 w-3" /> FAA VERIFIED</Badge>}
                            <Badge tone={STATUS_TONE[a.status] || "slate"}>{a.status?.toUpperCase()}</Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 mt-3 text-sm">
                            <div><div className="mono text-[10px] text-white/50 uppercase tracking-wider">Role</div><div className="mono">{(a.applicant_role || "").toUpperCase()}</div></div>
                            <div><div className="mono text-[10px] text-white/50 uppercase tracking-wider">Hours</div><div className="mono">{(a.applicant_hours || 0).toLocaleString()}</div></div>
                            <div><div className="mono text-[10px] text-white/50 uppercase tracking-wider">Based</div><div className="mono">{a.applicant_location || "—"}</div></div>
                            <div><div className="mono text-[10px] text-white/50 uppercase tracking-wider">Cert #</div><div className="mono">{a.faa_certificate || "—"}</div></div>
                          </div>
                          {a.applicant_email && (
                            <div className="mt-3 text-xs text-white/60 flex items-center gap-1.5"><Mail className="h-3 w-3" /> {a.applicant_email}</div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <button data-testid={`accept-${a.id}`} disabled={a.status === "accepted"} onClick={() => setStatus(a.id, "accepted", c.id)} className="btn btn-sm" style={{ borderColor: "rgba(46,213,115,0.4)", color: "#4EDF8B" }}>
                            <Check className="h-3.5 w-3.5" /> ACCEPT
                          </button>
                          <button data-testid={`decline-${a.id}`} disabled={a.status === "declined"} onClick={() => setStatus(a.id, "declined", c.id)} className="btn btn-sm" style={{ borderColor: "rgba(235,87,87,0.4)", color: "#EB5757" }}>
                            <XIcon className="h-3.5 w-3.5" /> DECLINE
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      {sub === "sent" && (
        <div className="space-y-3">
          {applied.length === 0 && <EmptyState icon={Send} title="No applications sent" hint="Apply to a contract from the Contracts page — track responses here." />}
          {applied.map((a) => (
            <Card key={a.id} testId={`sent-${a.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <Badge tone={STATUS_TONE[a.status] || "slate"}>{a.status?.toUpperCase()}</Badge>
                    <div className="mono text-[10px] tracking-widest text-white/40 uppercase">APPLIED {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="font-display text-lg font-semibold">{a.contract_title}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
